import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import AlphaplanIntegrationForm from './AlphaplanIntegrationForm.tsx';
import { buildAlphaplanMutationVariables } from './mutationVariables.ts';
import { CREATE_ALPHAPLAN_INTEGRATION, GET_ALPHAPLAN_INTEGRATIONS } from './queries.ts';
import type { AlphaplanFormData } from './types.ts';
import Button from '../../../core/components/Button.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_INTEGRATION_ALPHAPLAN_PATH } from '../../constants.ts';

const FORM_ID = 'alphaplan-integration-form';

const AlphaplanIntegrationAddPage = () => {
    const { t } = useTranslation('integrations');
    const navigate = useNavigate();
    const { publishToast } = useToaster();

    const backTooltipText = `${t('backTooltipText')} ${t('alphaplan.title')}`;

    const [createIntegration, { loading: isSubmitting }] = useMutation(CREATE_ALPHAPLAN_INTEGRATION, {
        refetchQueries: [GET_ALPHAPLAN_INTEGRATIONS],
    });

    const handleCreateIntegration = async (formData: AlphaplanFormData) => {
        try {
            await createIntegration({ variables: buildAlphaplanMutationVariables(formData) });
            publishToast({ description: t('alphaplan.createSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_ALPHAPLAN_PATH));
        } catch (error) {
            console.error(error);
            // the backend rejects invalid arguments with a readable GraphQL error, e.g. a bad base URL
            const reason = error instanceof Error ? error.message : '';
            publishToast({
                description: reason ? `${t('alphaplan.createError')}: ${reason}` : t('alphaplan.createError'),
                status: 'error',
            });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('alphaplan.title'), to: url(CUSTOMER_INTEGRATION_ALPHAPLAN_PATH) },
                            { label: t('alphaplan.form.createTitle') },
                        ]}
                    />

                    <div className="flex gap-2">
                        <Button type="button" asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_ALPHAPLAN_PATH)}>
                                {t('alphaplan.form.cancelButton')}
                            </Link>
                        </Button>
                        <Button variant="primary" type="submit" form={FORM_ID} loading={isSubmitting}>
                            {t('alphaplan.form.createButton')}
                        </Button>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6">
                    <AlphaplanIntegrationForm onSubmit={handleCreateIntegration} formId={FORM_ID} />
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default AlphaplanIntegrationAddPage;
